import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { theme, cardStyle, inputStyle, statCard } from '../theme';
import SOPTasksWidget from '../components/SOPTasksWidget';
import NABHComplianceWidget from '../components/NABHComplianceWidget';
import SecurityAlertsWidget from '../components/security/SecurityAlertsWidget';
import TodaysGoalsCard from '../components/TodaysGoalsCard';
import { FlaskConical, Crown, AlertTriangle, TrendingUp, Wallet, PhoneCall } from 'lucide-react';

/**
 * Dashboard tab — KPI cards + WhatsApp quick-send + Doctor Performance.
 *
 * Props:
 *   currentUser      — { fullName }
 *   dashboardStats   — { totalPatients, todaysPatients, todaysRevenue, totalRevenue }
 *   patientsList     — array of patients (used by WhatsApp search)
 *   API_BASE         — API base URL
 */
const DashboardPage = ({ currentUser, dashboardStats, patientsList, API_BASE, onNavigate }) => {
  const [waSearch, setWaSearch] = useState('');
  const [waSelectedPt, setWaSelectedPt] = useState(null);
  const [waCustomMsg, setWaCustomMsg] = useState('');

  // Advanced KPIs
  const [advancedKPIs, setAdvancedKPIs] = useState({
    productionGoal: 0, productionTarget: 500000,
    caseAcceptanceRate: 0,
    collectionRate: 0,
    newPatientsThisMonth: 0,
  });

  // Doctor performance data
  const [doctorPerf, setDoctorPerf] = useState([]);
  const [perfLoading, setPerfLoading] = useState(false);

  // Business intelligence — pulled from analytics-engine + credit/ageing
  const [bi, setBi] = useState({
    ageing: { current:0, '0-30':0, '31-60':0, '61-90':0, '90+':0, grand:0, patients:0 },
    ltv:    { topRows: [], totals: { patients:0, totalValue:0, avgLtv:0 }, segments: {} },
    abc:    { topRows: [], summary: { A:0, B:0, C:0, grand:0 } },
    cashFlow: { next30: 0, outstandingBalance: 0 },
  });
  const [biLoading, setBiLoading] = useState(false);

  useEffect(() => {
    if (!API_BASE) return;
    setBiLoading(true);
    Promise.all([
      axios.get(`${API_BASE}/credit/ageing/summary`).catch(()=>({data:{}})),
      axios.get(`${API_BASE}/analytics-engine/patient-ltv?limit=5`).catch(()=>({data:{top:[],totals:{},segments:{}}})),
      axios.get(`${API_BASE}/analytics-engine/abc?entity=treatments`).catch(()=>({data:{rows:[],summary:{}}})),
      axios.get(`${API_BASE}/analytics-engine/cash-flow-forecast?days=30`).catch(()=>({data:{rows:[],totals:{},outstandingBalance:0}})),
    ]).then(([ag, ltv, abc, cf]) => {
      setBi({
        ageing: ag.data || {},
        ltv: { topRows: ltv.data?.top || [], totals: ltv.data?.totals || {}, segments: ltv.data?.segments || {} },
        abc: { topRows: (abc.data?.rows || []).slice(0, 5), summary: abc.data?.summary || {} },
        cashFlow: {
          next30: Number(cf.data?.totals?.total || 0),
          outstandingBalance: Number(cf.data?.outstandingBalance || 0),
        },
      });
    }).finally(() => setBiLoading(false));
  }, [API_BASE]);

  // Calling Mode — today's due callback count
  const [callingDue, setCallingDue] = useState(0);
  useEffect(() => {
    if (!API_BASE) return;
    const load = () => axios.get(`${API_BASE}/calling-mode/stats`).then(r => setCallingDue(r.data?.dueToday || 0)).catch(() => {});
    load();
    const iv = setInterval(load, 90000);
    return () => clearInterval(iv);
  }, [API_BASE]);

  // Lab pending dispatch summary
  const [labSummary, setLabSummary] = useState({ pending:0, pendingOverdue:0 });
  useEffect(() => {
    if (!API_BASE) return;
    const load = () => axios.get(`${API_BASE}/lab/orders/summary`).then(r => setLabSummary(r.data)).catch(()=>{});
    load();
    const iv = setInterval(load, 60000); // refresh every minute
    return () => clearInterval(iv);
  }, [API_BASE]);

  useEffect(() => {
    if (!API_BASE) return;
    setPerfLoading(true);
    // Fetch doctors and treatments to compute performance
    Promise.all([
      axios.get(`${API_BASE}/doctors`).catch(() => ({ data: [] })),
      axios.get(`${API_BASE}/treatments`).catch(() => ({ data: [] })),
      axios.get(`${API_BASE}/bills`).catch(() => ({ data: [] })),
      axios.get(`${API_BASE}/payments`).catch(() => ({ data: [] })),
    ]).then(([docRes, txRes, billRes, payRes]) => {
      const treatments = txRes.data || [];
      const bills = billRes.data || [];
      const payments = payRes.data || [];

      // Current month boundaries
      const now = new Date();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);

      // Production goal: total billed this month
      const billsThisMonth = bills.filter(b => {
        const d = new Date(b.date || b.createdAt);
        return d >= monthStart && d <= monthEnd;
      });
      const totalBilledThisMonth = billsThisMonth.reduce((s, b) => s + (b.totalAmount || 0), 0);

      // Case acceptance rate: treatments done / all treatments * 100
      const treatmentsDone = treatments.filter(t => t.status === 'done').length;
      const totalTreatments = treatments.length;
      const caseAcceptance = totalTreatments > 0 ? Math.round((treatmentsDone / totalTreatments) * 100) : 0;

      // Collection rate: payments received this month / bills generated this month * 100
      const paymentsThisMonth = payments.filter(p => {
        const d = new Date(p.date || p.createdAt);
        return d >= monthStart && d <= monthEnd;
      });
      const totalPaymentsThisMonth = paymentsThisMonth.reduce((s, p) => s + (p.amount || 0), 0);
      const totalBillsGenerated = billsThisMonth.reduce((s, b) => s + (b.totalAmount || 0), 0);
      const collectionRate = totalBillsGenerated > 0 ? Math.round((totalPaymentsThisMonth / totalBillsGenerated) * 100) : 0;

      // New patients this month
      const newPatientsCount = (patientsList || []).filter(p => {
        const d = new Date(p.patientSince || p.createdAt);
        return d >= monthStart && d <= monthEnd;
      }).length;

      setAdvancedKPIs({
        productionGoal: totalBilledThisMonth,
        productionTarget: 500000,
        caseAcceptanceRate: caseAcceptance,
        collectionRate: Math.min(collectionRate, 100),
        newPatientsThisMonth: newPatientsCount,
      });
    }).catch(() => {});
  }, [API_BASE, patientsList]);

  useEffect(() => {
    if (!API_BASE) return;
    setPerfLoading(true);
    // Fetch doctors and treatments to compute performance
    Promise.all([
      axios.get(`${API_BASE}/doctors`).catch(() => ({ data: [] })),
      axios.get(`${API_BASE}/treatments`).catch(() => ({ data: [] })),
    ]).then(([docRes, txRes]) => {
      const doctors = docRes.data || [];
      const treatments = txRes.data || [];
      const perfMap = {};

      // Initialize with known doctors
      doctors.forEach(d => {
        const name = d.name || d.fullName || 'Unknown';
        perfMap[name] = { name, patients: new Set(), revenue: 0, treatments: 0 };
      });

      // Aggregate treatment data per doctor
      treatments.forEach(tx => {
        const docName = tx.doctor || tx.doctorName || 'Dr. Sumit Kumar';
        if (!perfMap[docName]) {
          perfMap[docName] = { name: docName, patients: new Set(), revenue: 0, treatments: 0 };
        }
        perfMap[docName].treatments += 1;
        perfMap[docName].revenue += (tx.totalAmount || 0);
        if (tx.patientId) perfMap[docName].patients.add(tx.patientId);
      });

      // If no data at all, show at least the current user
      if (Object.keys(perfMap).length === 0) {
        perfMap[currentUser.fullName || 'Dr. Sumit Kumar'] = {
          name: currentUser.fullName || 'Dr. Sumit Kumar',
          patients: new Set(),
          revenue: dashboardStats.totalRevenue || 0,
          treatments: 0,
        };
      }

      const perfArr = Object.values(perfMap).map(d => ({
        ...d,
        patients: d.patients.size || 0,
      })).sort((a, b) => b.revenue - a.revenue);

      setDoctorPerf(perfArr);
    }).catch(() => {}).finally(() => setPerfLoading(false));
  }, [API_BASE, currentUser.fullName, dashboardStats.totalRevenue]);

  const stats = [
    { label:'Total Patients', val:dashboardStats.totalPatients, color:theme.primary, icon:'👥' },
    { label:"Today's New",    val:dashboardStats.todaysPatients, color:theme.info,    icon:'✨' },
    { label:"Today's Fees",   val:`₹ ${dashboardStats.todaysRevenue}`, color:theme.success, icon:'💰' },
    { label:'Total Revenue',  val:`₹ ${dashboardStats.totalRevenue}`, color:theme.purple, icon:'📈' },
  ];

  const filteredPatients = patientsList.filter(p =>
    (p.name||'').toLowerCase().includes(waSearch.toLowerCase()) ||
    (p.phone||'').includes(waSearch)
  );

  const sendDefault = () => {
    if (!waSelectedPt?.phone) return alert('Please select a patient first.');
    const msg = waCustomMsg.trim() || `Hello ${waSelectedPt.name}, this is a message from Siddharth Dental Clinic. Please feel free to contact us for any dental concerns. Thank you!`;
    window.open(`https://web.whatsapp.com/send?phone=91${waSelectedPt.phone}&text=${encodeURIComponent(msg)}`,'_blank');
  };

  const sendSummary = () => {
    if (!waSelectedPt?.phone) return alert('Please select a patient first.');
    const sinceDate = waSelectedPt.patientSince ? new Date(waSelectedPt.patientSince).toLocaleDateString('en-IN') : 'N/A';
    const msg = `Hello *${waSelectedPt.name}*,\n\n🦷 *Siddharth Dental Clinic*\nHere is your patient summary:\n\n📋 UHID: ${waSelectedPt.uhid||'N/A'}\n📅 Patient Since: ${sinceDate}\n📞 Phone: ${waSelectedPt.phone}\n\nPlease contact us for any appointment or query.\nThank you!`;
    window.open(`https://web.whatsapp.com/send?phone=91${waSelectedPt.phone}&text=${encodeURIComponent(msg)}`,'_blank');
  };

  return (
    <div>
      <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'24px'}}>
        <div>
          <h2 style={{margin:0, color:theme.navy, fontSize:'1.5rem', fontWeight:'700'}}>Clinic Overview</h2>
          <p style={{margin:'4px 0 0', color:theme.textMuted, fontSize:'0.88rem'}}>Welcome back, {currentUser.fullName}</p>
        </div>
      </div>

      <TodaysGoalsCard
        API_BASE={API_BASE}
        currentUser={currentUser}
        monthlyTarget={advancedKPIs.productionTarget}
        todaysRevenue={dashboardStats.todaysRevenue}
        onNavigate={onNavigate}
      />

      {/* Calling Mode — always-visible tile. Louder when callbacks are due. */}
      <div
        onClick={() => onNavigate && onNavigate('calling-mode')}
        style={{
          ...cardStyle,
          marginBottom: '18px',
          cursor: 'pointer',
          borderLeft: `5px solid ${callingDue > 0 ? theme.primary : theme.secondary}`,
          backgroundColor: callingDue > 0 ? theme.primaryLight : theme.sectionBlue,
          display: 'flex',
          alignItems: 'center',
          gap: '14px',
        }}
        title="Open Calling Mode"
      >
        <div style={{
          width: '44px', height: '44px', borderRadius: '12px',
          backgroundColor: (callingDue > 0 ? theme.primary : theme.secondary) + '22',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <PhoneCall size={22} color={callingDue > 0 ? theme.primary : theme.secondary} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, color: theme.navy, fontSize: '0.98rem' }}>
            Calling Mode
            {callingDue > 0 && (
              <span style={{ marginLeft: 10, padding: '2px 10px', borderRadius: 10, backgroundColor: theme.primary, color: 'white', fontSize: '0.72rem' }}>
                {callingDue} due today
              </span>
            )}
          </div>
          <div style={{ fontSize: '0.82rem', color: theme.textMuted, marginTop: 2 }}>
            {callingDue > 0
              ? 'Aaj in patients ko call karna hai — ek tap me done'
              : 'Patient call log + callback reminders'}
          </div>
        </div>
        <div style={{ fontSize: '0.85rem', color: theme.primary, fontWeight: 700 }}>Open →</div>
      </div>

      <SecurityAlertsWidget API_BASE={API_BASE} onNavigate={onNavigate} />

      <NABHComplianceWidget API_BASE={API_BASE} onNavigate={onNavigate} />

      <SOPTasksWidget API_BASE={API_BASE} currentUser={currentUser} onNavigate={onNavigate} />

      {/* Pending Lab Dispatch — shown only when there are pending cases */}
      {labSummary.pending > 0 && (
        <div
          onClick={()=>onNavigate && onNavigate('lab')}
          style={{
            ...cardStyle, marginBottom:'18px', cursor:'pointer',
            borderLeft: `5px solid ${labSummary.pendingOverdue > 0 ? theme.danger : theme.warning}`,
            backgroundColor: labSummary.pendingOverdue > 0 ? '#fff5f5' : '#fffbeb',
            display:'flex', alignItems:'center', gap:'14px',
          }}
          title="Open Lab Work"
        >
          <div style={{
            width:'44px', height:'44px', borderRadius:'12px',
            backgroundColor: (labSummary.pendingOverdue > 0 ? theme.danger : theme.warning) + '22',
            display:'flex', alignItems:'center', justifyContent:'center',
          }}>
            <FlaskConical size={22} color={labSummary.pendingOverdue > 0 ? theme.danger : theme.warning}/>
          </div>
          <div style={{flex:1}}>
            <div style={{fontWeight:'700', color:theme.navy, fontSize:'0.95rem'}}>
              {labSummary.pending} lab case{labSummary.pending>1?'s':''} pending dispatch
              {labSummary.pendingOverdue > 0 && (
                <span style={{marginLeft:'10px', padding:'2px 10px', borderRadius:'10px', backgroundColor:theme.danger, color:'white', fontSize:'0.72rem'}}>
                  {labSummary.pendingOverdue} overdue
                </span>
              )}
            </div>
            <div style={{fontSize:'0.8rem', color:theme.textMuted, marginTop:'2px'}}>
              Scans done but not yet sent to lab — tap to review and dispatch
            </div>
          </div>
          <div style={{fontSize:'0.85rem', color:theme.primary, fontWeight:'700'}}>Open →</div>
        </div>
      )}

      <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:'18px', marginBottom:'24px' }}>
        {stats.map((s,i)=>(
          <div key={i} style={statCard}>
            <div style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
              <span style={{fontSize:'0.78rem', color:theme.textMuted, fontWeight:'600', textTransform:'uppercase', letterSpacing:'0.04em'}}>{s.label}</span>
              <div style={{
                width:'34px', height:'34px',
                borderRadius:'10px',
                backgroundColor: s.color+'18',
                display:'flex', alignItems:'center', justifyContent:'center',
                fontSize:'1.05rem',
              }}>{s.icon}</div>
            </div>
            <h2 style={{margin:'6px 0 0', color:theme.navy, fontSize:'1.6rem', fontWeight:'700'}}>{s.val}</h2>
          </div>
        ))}
      </div>

      {/* Advanced Analytics KPIs */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:'18px', marginBottom:'24px' }}>
        {/* Production Goal */}
        <div style={statCard}>
          <div style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
            <span style={{fontSize:'0.78rem', color:theme.textMuted, fontWeight:'600', textTransform:'uppercase', letterSpacing:'0.04em'}}>Production Goal</span>
            <div style={{width:'34px', height:'34px', borderRadius:'10px', backgroundColor:theme.success+'18', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.05rem'}}>
              🎯
            </div>
          </div>
          <h2 style={{margin:'6px 0 0', color:theme.navy, fontSize:'1.3rem', fontWeight:'700'}}>
            {`\u20B9${advancedKPIs.productionGoal.toLocaleString('en-IN')}`}
          </h2>
          <div style={{marginTop:'6px', height:'6px', borderRadius:'3px', backgroundColor:'#e9ecef', overflow:'hidden'}}>
            <div style={{height:'100%', borderRadius:'3px', backgroundColor: advancedKPIs.productionGoal >= advancedKPIs.productionTarget ? theme.success : theme.warning,
              width:`${Math.min((advancedKPIs.productionGoal / advancedKPIs.productionTarget) * 100, 100)}%`, transition:'width 0.5s'}}/>
          </div>
          <span style={{fontSize:'0.72rem', color:theme.textMuted, marginTop:'4px', display:'block'}}>
            of {`\u20B9${advancedKPIs.productionTarget.toLocaleString('en-IN')}`} target ({Math.round((advancedKPIs.productionGoal / advancedKPIs.productionTarget) * 100)}%)
          </span>
        </div>

        {/* Case Acceptance Rate */}
        <div style={statCard}>
          <div style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
            <span style={{fontSize:'0.78rem', color:theme.textMuted, fontWeight:'600', textTransform:'uppercase', letterSpacing:'0.04em'}}>Case Acceptance</span>
            <div style={{width:'34px', height:'34px', borderRadius:'10px', backgroundColor:theme.info+'18', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.05rem'}}>
              ✅
            </div>
          </div>
          <h2 style={{margin:'6px 0 0', color: advancedKPIs.caseAcceptanceRate >= 70 ? theme.success : theme.warning, fontSize:'1.6rem', fontWeight:'700'}}>
            {advancedKPIs.caseAcceptanceRate}%
          </h2>
          <span style={{fontSize:'0.72rem', color:theme.textMuted}}>Treatments done vs planned</span>
        </div>

        {/* Collection Rate */}
        <div style={statCard}>
          <div style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
            <span style={{fontSize:'0.78rem', color:theme.textMuted, fontWeight:'600', textTransform:'uppercase', letterSpacing:'0.04em'}}>Collection Rate</span>
            <div style={{width:'34px', height:'34px', borderRadius:'10px', backgroundColor:(theme.purple || '#7c3aed')+'18', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.05rem'}}>
              💳
            </div>
          </div>
          <h2 style={{margin:'6px 0 0', color: advancedKPIs.collectionRate >= 80 ? theme.success : theme.danger, fontSize:'1.6rem', fontWeight:'700'}}>
            {advancedKPIs.collectionRate}%
          </h2>
          <span style={{fontSize:'0.72rem', color:theme.textMuted}}>Payments received vs billed</span>
        </div>

        {/* New Patients This Month */}
        <div style={statCard}>
          <div style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
            <span style={{fontSize:'0.78rem', color:theme.textMuted, fontWeight:'600', textTransform:'uppercase', letterSpacing:'0.04em'}}>New Patients</span>
            <div style={{width:'34px', height:'34px', borderRadius:'10px', backgroundColor:theme.primary+'18', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.05rem'}}>
              🆕
            </div>
          </div>
          <h2 style={{margin:'6px 0 0', color:theme.navy, fontSize:'1.6rem', fontWeight:'700'}}>
            {advancedKPIs.newPatientsThisMonth}
          </h2>
          <span style={{fontSize:'0.72rem', color:theme.textMuted}}>Registered this month</span>
        </div>
      </div>

      {/* ── Business Intelligence ───────────────────────────────────────── */}
      <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', margin:'24px 0 12px'}}>
        <h3 style={{margin:0, color:theme.navy, fontSize:'1.05rem', fontWeight:'700'}}>Business Intelligence</h3>
        {onNavigate && (
          <button
            onClick={()=>onNavigate('analytics-hub')}
            style={{padding:'6px 14px', backgroundColor:'transparent', border:`1px solid ${theme.border}`, borderRadius:'8px', color:theme.primary, fontWeight:'600', fontSize:'0.82rem', cursor:'pointer'}}
          >Open Analytics Hub →</button>
        )}
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:'18px', marginBottom:'18px' }}>
        {/* Total Receivables */}
        <div
          onClick={()=>onNavigate && onNavigate('financial')}
          style={{...statCard, cursor: onNavigate ? 'pointer' : 'default'}}
          title="Open Financial Controls"
        >
          <div style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
            <span style={{fontSize:'0.78rem', color:theme.textMuted, fontWeight:'600', textTransform:'uppercase', letterSpacing:'0.04em'}}>Receivables</span>
            <div style={{width:'34px', height:'34px', borderRadius:'10px', backgroundColor:theme.warning+'18', display:'flex', alignItems:'center', justifyContent:'center'}}>
              <Wallet size={18} color={theme.warning}/>
            </div>
          </div>
          <h2 style={{margin:'6px 0 0', color:theme.navy, fontSize:'1.4rem', fontWeight:'700'}}>
            {`\u20B9${Number(bi.ageing.grand||0).toLocaleString('en-IN')}`}
          </h2>
          <span style={{fontSize:'0.72rem', color:theme.textMuted}}>
            across {bi.ageing.patients||0} patient{bi.ageing.patients===1?'':'s'}
          </span>
        </div>

        {/* 90+ Overdue */}
        <div style={statCard}>
          <div style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
            <span style={{fontSize:'0.78rem', color:theme.textMuted, fontWeight:'600', textTransform:'uppercase', letterSpacing:'0.04em'}}>90+ Overdue</span>
            <div style={{width:'34px', height:'34px', borderRadius:'10px', backgroundColor:theme.danger+'18', display:'flex', alignItems:'center', justifyContent:'center'}}>
              <AlertTriangle size={18} color={theme.danger}/>
            </div>
          </div>
          <h2 style={{margin:'6px 0 0', color: (bi.ageing['90+']||0) > 0 ? theme.danger : theme.navy, fontSize:'1.4rem', fontWeight:'700'}}>
            {`\u20B9${Number(bi.ageing['90+']||0).toLocaleString('en-IN')}`}
          </h2>
          <span style={{fontSize:'0.72rem', color:theme.textMuted}}>chase priority — write-off candidates</span>
        </div>

        {/* VIP / Loyal Patients */}
        <div style={statCard}>
          <div style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
            <span style={{fontSize:'0.78rem', color:theme.textMuted, fontWeight:'600', textTransform:'uppercase', letterSpacing:'0.04em'}}>VIP / Loyal</span>
            <div style={{width:'34px', height:'34px', borderRadius:'10px', backgroundColor:theme.purple+'18', display:'flex', alignItems:'center', justifyContent:'center'}}>
              <Crown size={18} color={theme.purple}/>
            </div>
          </div>
          <h2 style={{margin:'6px 0 0', color:theme.navy, fontSize:'1.4rem', fontWeight:'700'}}>
            {(bi.ltv.segments?.vip||0) + (bi.ltv.segments?.loyal||0)}
          </h2>
          <span style={{fontSize:'0.72rem', color:theme.textMuted}}>
            {bi.ltv.segments?.vip||0} VIP · {bi.ltv.segments?.loyal||0} loyal · {bi.ltv.segments?.dormant||0} dormant
          </span>
        </div>

        {/* 30-day Cash Inflow Forecast */}
        <div style={statCard}>
          <div style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
            <span style={{fontSize:'0.78rem', color:theme.textMuted, fontWeight:'600', textTransform:'uppercase', letterSpacing:'0.04em'}}>30-Day Forecast</span>
            <div style={{width:'34px', height:'34px', borderRadius:'10px', backgroundColor:theme.success+'18', display:'flex', alignItems:'center', justifyContent:'center'}}>
              <TrendingUp size={18} color={theme.success}/>
            </div>
          </div>
          <h2 style={{margin:'6px 0 0', color:theme.success, fontSize:'1.4rem', fontWeight:'700'}}>
            {`\u20B9${Math.round(bi.cashFlow.next30||0).toLocaleString('en-IN')}`}
          </h2>
          <span style={{fontSize:'0.72rem', color:theme.textMuted}}>projected cash inflow</span>
        </div>
      </div>

      {/* Top patients + Top treatments side-by-side */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'18px', marginBottom:'24px' }}>
        {/* Top 5 patients by lifetime spend */}
        <div style={cardStyle}>
          <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'12px'}}>
            <h4 style={{margin:0, color:theme.navy, fontSize:'0.95rem', fontWeight:'700'}}>Top Patients (Lifetime Value)</h4>
            <span style={{fontSize:'0.72rem', color:theme.textMuted}}>
              avg LTV: {`\u20B9${Math.round(bi.ltv.totals?.avgLtv||0).toLocaleString('en-IN')}`}
            </span>
          </div>
          {biLoading ? (
            <p style={{color:theme.textMuted, fontSize:'0.85rem', margin:0}}>Loading…</p>
          ) : bi.ltv.topRows.length === 0 ? (
            <p style={{color:theme.textSubtle, fontSize:'0.85rem', margin:0}}>No patient billing data yet</p>
          ) : (
            <div style={{display:'flex', flexDirection:'column', gap:'8px'}}>
              {bi.ltv.topRows.map((p, i) => (
                <div key={i} style={{display:'flex', alignItems:'center', justifyContent:'space-between', padding:'8px 12px', borderRadius:'8px', backgroundColor: i===0 ? theme.primaryLight : '#fafbfc'}}>
                  <div style={{display:'flex', alignItems:'center', gap:'10px', minWidth:0, flex:1}}>
                    <span style={{fontSize:'0.78rem', fontWeight:'700', color: i===0 ? theme.primary : theme.textMuted, minWidth:'18px'}}>{i+1}.</span>
                    <div style={{minWidth:0, flex:1}}>
                      <div style={{fontWeight:'600', color:theme.navy, fontSize:'0.85rem', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{p.name}</div>
                      <div style={{fontSize:'0.72rem', color:theme.textMuted}}>{p.visits} visits · last {p.recencyDays}d ago</div>
                    </div>
                    {p.segment && (
                      <span style={{fontSize:'0.65rem', padding:'2px 8px', borderRadius:'10px', textTransform:'uppercase', fontWeight:'700',
                        backgroundColor: p.segment==='vip'?theme.purple+'22':p.segment==='loyal'?theme.success+'22':p.segment==='dormant'?theme.danger+'22':theme.border,
                        color: p.segment==='vip'?theme.purple:p.segment==='loyal'?theme.success:p.segment==='dormant'?theme.danger:theme.textMuted,
                      }}>{p.segment}</span>
                    )}
                  </div>
                  <span style={{fontWeight:'700', color:theme.navy, fontSize:'0.9rem', marginLeft:'10px'}}>
                    {`\u20B9${Number(p.totalSpend||0).toLocaleString('en-IN')}`}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Top 5 treatments by revenue (ABC) */}
        <div style={cardStyle}>
          <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'12px'}}>
            <h4 style={{margin:0, color:theme.navy, fontSize:'0.95rem', fontWeight:'700'}}>Top Treatments (Revenue)</h4>
            <span style={{fontSize:'0.72rem', color:theme.textMuted}}>
              {bi.abc.summary?.A||0}A · {bi.abc.summary?.B||0}B · {bi.abc.summary?.C||0}C
            </span>
          </div>
          {biLoading ? (
            <p style={{color:theme.textMuted, fontSize:'0.85rem', margin:0}}>Loading…</p>
          ) : bi.abc.topRows.length === 0 ? (
            <p style={{color:theme.textSubtle, fontSize:'0.85rem', margin:0}}>No billed treatments yet</p>
          ) : (
            <div style={{display:'flex', flexDirection:'column', gap:'8px'}}>
              {bi.abc.topRows.map((t, i) => (
                <div key={i} style={{display:'flex', alignItems:'center', justifyContent:'space-between', padding:'8px 12px', borderRadius:'8px', backgroundColor: i===0 ? theme.secondaryLight : '#fafbfc'}}>
                  <div style={{display:'flex', alignItems:'center', gap:'10px', minWidth:0, flex:1}}>
                    <span style={{fontSize:'0.78rem', fontWeight:'700', color: i===0 ? theme.secondary : theme.textMuted, minWidth:'18px'}}>{i+1}.</span>
                    <div style={{minWidth:0, flex:1}}>
                      <div style={{fontWeight:'600', color:theme.navy, fontSize:'0.85rem', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{t.name}</div>
                      <div style={{fontSize:'0.72rem', color:theme.textMuted}}>{t.count||0} units · {t.cumPct||0}% cum.</div>
                    </div>
                    {t.class && (
                      <span style={{fontSize:'0.65rem', padding:'2px 8px', borderRadius:'10px', fontWeight:'700',
                        backgroundColor: t.class==='A'?theme.success+'22':t.class==='B'?theme.warning+'22':theme.border,
                        color: t.class==='A'?theme.success:t.class==='B'?theme.warning:theme.textMuted,
                      }}>{t.class}</span>
                    )}
                  </div>
                  <span style={{fontWeight:'700', color:theme.navy, fontSize:'0.9rem', marginLeft:'10px'}}>
                    {`\u20B9${Number(t.revenue||0).toLocaleString('en-IN')}`}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Doctor Performance */}
      <div style={{...cardStyle, marginBottom:'20px'}}>
        <h3 style={{color:theme.navy, margin:'0 0 14px', fontSize:'1.05rem', fontWeight:'700'}}>Doctor Performance</h3>
        {perfLoading ? (
          <p style={{color:theme.textMuted, fontSize:'0.88rem'}}>Loading...</p>
        ) : doctorPerf.length === 0 ? (
          <p style={{color:'#bbb', fontSize:'0.88rem'}}>No performance data available</p>
        ) : (
          <div style={{display:'grid', gridTemplateColumns:`repeat(${Math.min(doctorPerf.length, 3)}, 1fr)`, gap:'14px'}}>
            {doctorPerf.map((doc, i) => (
              <div key={i} style={{padding:'16px', borderRadius:'12px', backgroundColor:theme.surface || '#fafafa', border:`1px solid ${theme.border || '#eee'}`, boxShadow:'0 1px 4px rgba(0,0,0,0.06)'}}>
                <div style={{fontWeight:'700', color:theme.navy, fontSize:'0.95rem', marginBottom:'12px'}}>{doc.name}</div>
                <div style={{display:'flex', flexDirection:'column', gap:'8px'}}>
                  <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                    <span style={{fontSize:'0.78rem', color:theme.textMuted || '#888', fontWeight:'600'}}>Patients Seen</span>
                    <span style={{fontWeight:'700', color:theme.primary, fontSize:'1.05rem'}}>{doc.patients}</span>
                  </div>
                  <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                    <span style={{fontSize:'0.78rem', color:theme.textMuted || '#888', fontWeight:'600'}}>Revenue</span>
                    <span style={{fontWeight:'700', color:theme.success, fontSize:'1.05rem'}}>
                      {`\u20B9${(doc.revenue || 0).toLocaleString('en-IN')}`}
                    </span>
                  </div>
                  <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                    <span style={{fontSize:'0.78rem', color:theme.textMuted || '#888', fontWeight:'600'}}>Treatments</span>
                    <span style={{fontWeight:'700', color:theme.info || '#0582ca', fontSize:'1.05rem'}}>{doc.treatments}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* WhatsApp Status Card */}
      <div style={cardStyle}>
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
          <div>
            <h3 style={{color:theme.navy, margin:'0 0 4px', fontSize:'1.05rem', fontWeight:'700'}}>💬 WhatsApp (Official API)</h3>
            <p style={{fontSize:'0.82rem', color:theme.textMuted, margin:0}}>Auto-sends: Appointment, Treatment, Payment, Birthday — all background me</p>
          </div>
          <div style={{padding:'6px 14px', borderRadius:'8px', backgroundColor:'#f0fdf4', border:'1px solid #bbf7d0', fontSize:'0.82rem', fontWeight:'700', color:'#16a34a'}}>
            ✓ Active
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
